import { useMemo, useState } from 'react';
import type { Cell, ConceptRow } from '@balance/domain';
import { CellDetail } from '../components/CellDetail.js';
import { Badge, ConceptName, StatusDot, cellClass } from '../components/ui.js';
import { YearStrip } from './Monthly.js';
import { eur, monthName } from '../format.js';
import { useApp } from '../state.js';

export function GridPage() {
  const { balance, setPeriod } = useApp();
  const [detail, setDetail] = useState<{ row: ConceptRow; cell: Cell } | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const groups = useMemo(() => {
    if (!balance) return [];
    const out: Array<{ key: string; name: string; rows: ConceptRow[] }> = [];
    for (const row of balance.rows) {
      if (!showInactive && !row.everActive && !Object.values(row.cells).some((c) => c.actualCents !== 0)) continue;
      const key = row.direction === 'income' ? 'income' : row.categoryId ?? 'none';
      let g = out.find((x) => x.key === key);
      if (!g) {
        g = { key, name: row.direction === 'income' ? 'Ingresos' : row.categoryName, rows: [] };
        out.push(g);
      }
      g.rows.push(row);
    }
    return out;
  }, [balance, showInactive]);
  if (!balance) return <div className="skeleton" />;
  const periods = balance.periods;
  const hasFinancing = periods.some((p) => balance.totals[p.period]!.financingCents !== 0);
  const hasPending = periods.some((p) => balance.totals[p.period]!.pendingOutCents !== 0 || balance.totals[p.period]!.pendingInCents !== 0);
  const colClass = (status: string) => (status === 'future' ? 'projected' : status === 'current' ? 'current' : '');
  const subtotal = (label: string, pick: (p: string) => number | null, cls?: (v: number) => string) => (
    <tr className="subtotal">
      <td>{label}</td>
      {periods.map((p) => {
        const v = pick(p.period);
        return (
          <td key={p.period} className={`${colClass(p.status)} ${v !== null && cls ? cls(v) : ''}`}>
            <button type="button" onClick={() => setPeriod(p.period)} title={`Ir a ${p.label}`}>
              {eur(v)}
            </button>
          </td>
        );
      })}
    </tr>
  );
  return (
    <div className="page">
      <YearStrip />
      <div className="panel">
        <div className="panelhead">
          <div>
            <h2>Balance en grilla · {balance.horizon.from.slice(0, 4)}{balance.horizon.to.slice(0, 4) !== balance.horizon.from.slice(0, 4) ? `–${balance.horizon.to.slice(0, 4)}` : ''}</h2>
            <div className="muted">Mismas filas y orden que la vista mensual. Seleccioná una cifra para abrir su detalle; «—» es ausencia de vencimiento.</div>
          </div>
          <div className="inline">
            <Badge>{monthName(balance.horizon.from)} → {monthName(balance.horizon.to)} · proyección móvil de {balance.horizon.months} meses</Badge>
            <label className="check" style={{ padding: 0 }}>
              <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
              Mostrar inactivos
            </label>
          </div>
        </div>
        <div className="tablewrap">
          <table className="sheet">
            <thead>
              <tr>
                <th>Concepto</th>
                {periods.map((p) => (
                  <th key={p.period} className={colClass(p.status)}>
                    {p.shortLabel}
                    <div className="muted">{p.status === 'closed' ? 'Cerrado' : p.status === 'current' ? 'En curso' : 'Previsto'}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <GroupRows key={g.key} group={g} periods={periods} colClass={colClass} balanceCurrent={balance.currentPeriod} onCell={(row, cell) => setDetail({ row, cell })} afterRows={g.key === 'income' ? subtotal('Ingresos', (p) => balance.totals[p]!.incomeCents) : null} />
              ))}
              {subtotal('Egresos', (p) => balance.totals[p]!.expenseCents)}
              {hasPending ? subtotal('Sin clasificar (provisional)', (p) => balance.totals[p]!.pendingOutCents - balance.totals[p]!.pendingInCents) : null}
              {subtotal('Resto', (p) => balance.totals[p]!.resultCents, (v) => (v >= 0 ? 'positive' : 'negative'))}
              {hasFinancing ? subtotal('Préstamo recibido (suma al acumulado)', (p) => balance.totals[p]!.financingCents || null) : null}
              {subtotal('Acumulado', (p) => balance.totals[p]!.accumulatedCents)}
            </tbody>
          </table>
        </div>
      </div>
      <div className="hint">
        Acumulado encadenado desde {eur(balance.openingBalanceCents)} al inicio de {monthName(balance.openingPeriod)} {balance.openingPeriod.slice(0, 4)}. Los meses cerrados usan el real imputado; los abiertos, la previsión actualizada.
        {hasFinancing ? ' Lo recibido de un préstamo no cuenta en el resto (no es ahorro generado) pero sí suma al acumulado; las cuotas lo van descontando.' : ''}
      </div>
      {detail ? <CellDetail row={detail.row} cell={detail.cell} onClose={() => setDetail(null)} /> : null}
    </div>
  );
}

function GroupRows({ group, periods, colClass, onCell, afterRows, balanceCurrent }: { group: { key: string; name: string; rows: ConceptRow[] }; periods: Array<{ period: string; status: string }>; colClass: (s: string) => string; onCell: (row: ConceptRow, cell: Cell) => void; afterRows: React.ReactNode; balanceCurrent: string }) {
  return (
    <>
      <tr className="cat">
        <td colSpan={periods.length + 1}>{group.name}</td>
      </tr>
      {group.rows.map((row) => (
        <tr key={row.conceptId}>
          <td>
            <ConceptName id={row.conceptId} name={row.name} />
            {!row.activeNow && row.behavior !== 'one_off' && !(row.behavior === 'income' && !row.everActive) ? (
              <span className="badge neutral" style={{ marginLeft: 6 }}>
                {Object.values(row.cells).some((c) => c.period > balanceCurrent && c.status !== 'inactive') ? 'programado' : 'baja'}
              </span>
            ) : null}
          </td>
          {periods.map((p) => {
            const cell = row.cells[p.period]!;
            return (
              <td key={p.period} className={`${colClass(p.status)} ${cellClass(cell)}`}>
                <button type="button" onClick={() => onCell(row, cell)} title={cell.explanation[0] ?? ''} disabled={cell.status === 'inactive'}>
                  <StatusDot status={cell.status} />
                  {cell.status === 'inactive' ? '' : eur(cell.projectedCents)}
                </button>
              </td>
            );
          })}
        </tr>
      ))}
      {afterRows}
    </>
  );
}
